import type { DragLocationHistory } from "@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types";
import { z } from "zod";
import type { PlanTableFormat } from "../types/planning/plan.type";

/**
 * Determines drop header flag, the ID of the shipment after which the drop occurred,
 * and the subsequent stop sequence.
 */
export const getDropMetrics = (
  plan: PlanTableFormat,
  location: DragLocationHistory,
  destinationIndex: number,
  isDroppedOnHeader: boolean
): {
  isDroppedOnHeader: boolean;
  afterShipmentId?: number;
  afterRouteStop: number | null;
} => {
  // Determine edge (top/bottom) of the drop relative to the row
  const targetData = location.current.dropTargets[0].data;
  const edgeSymbol = Object.getOwnPropertySymbols(targetData)[0];
  const edge = z
    .enum(["top", "bottom"])
    .parse(targetData[edgeSymbol] ?? "bottom");

  // Prepare shipments sorted by stop sequence
  const sorted = [...plan.shipments].sort((a, b) => a.stopSeq - b.stopSeq);

  const idx = Number(destinationIndex);

  let afterShipmentId: number | undefined;
  let afterRouteStop: number | null;

  const isTrap = plan.type === "delivery_trap";

  if (isDroppedOnHeader || !idx) {
    // Dropped on header (before all shipments)
    afterShipmentId = undefined;
    afterRouteStop = null;
  } else if (edge === "top") {
    // Top-edge drop: the first slot (idx === 1) maps to 0, otherwise use previous element
    if (idx === 1) {
      afterShipmentId = isTrap ? undefined : 0;
      afterRouteStop = isTrap ? null : 0;
    } else {
      const prevIdx = Math.min(idx - 2, sorted.length - 1);
      const prev = sorted[prevIdx];
      afterShipmentId = isTrap ? undefined : prev.id;
      afterRouteStop = isTrap ? null : prev.stopSeq;
    }
  } else {
    const prevIdx = Math.min(idx - 1, sorted.length - 1);
    const prev = sorted[prevIdx];
    afterShipmentId = isTrap ? undefined : prev.id;
    afterRouteStop = isTrap ? null : prev.stopSeq;
  }

  return { isDroppedOnHeader, afterShipmentId, afterRouteStop };
};
